import React, { useState, useEffect } from 'react';
import { 
  X, Users, DollarSign, CheckCircle, AlertTriangle, 
  Send, RefreshCw, Bell, Calendar, Clock, Trash2,
  Edit, Eye, ChevronDown, ChevronUp, MessageCircle,
  TrendingUp, Layers, Plus
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useData } from '../../contexts/DataContext';
import { feeApi, whatsappAnalyticsApi } from '../../api/services';
import ConfirmationModal from '../../ui/ConfirmationModal';
import RecipientSelector from '../../components/Common/RecipientSelector';

// =========================================================================
// =========================================================================
const CampaignDetailModal = ({ campaign, onClose, feeStructure }) => {
  const [fetchedMetrics, setFetchedMetrics] = useState(null);
  const [loadingMetrics, setLoadingMetrics] = useState(false);

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!campaign?.scheduleDate) return;
      
      setLoadingMetrics(true);
      try {
        const startDate = new Date(campaign.scheduleDate).toISOString().split('T')[0];
        const endDate = new Date().toISOString().split('T')[0]; // Today
        
        // Fetch summary for the period since campaign started
        const res = await whatsappAnalyticsApi.getSummary({
          start_date: startDate,
          end_date: endDate,
          granularity: 'day'
        });

        if (res.success && res.data) {
           setFetchedMetrics(res.data);
        }
      } catch (error) {
        console.error("Failed to fetch campaign analytics", error);
      } finally {
        setLoadingMetrics(false);
      }
    };

    fetchAnalytics();
  }, [campaign]);

  if (!campaign) return null;

  // Use fetched metrics if available, otherwise fall back to campaign snapshot or defaults
  const metrics = fetchedMetrics || campaign.metrics || { total: 0, delivered: 0, failed: 0, read: 0, paidAfter: 0 };
  
  const deliveredRate = metrics.total > 0 ? ((metrics.delivered / metrics.total) * 100).toFixed(1) : 0;
  const readRate = metrics.delivered > 0 ? ((metrics.read / metrics.delivered) * 100).toFixed(1) : 0;
  const conversionRate = metrics.total > 0 ? ((metrics.paidAfter / metrics.total) * 100).toFixed(1) : 0;

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              {feeStructure?.name || 'Campaign Details'}
              {loadingMetrics && <RefreshCw className="h-3 w-3 animate-spin text-gray-400" />}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">Performance metrics and analytics</p>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-medium text-blue-700">Recipients</p>
                  <p className="text-xl font-bold text-blue-900 mt-0.5">{metrics.total || metrics.sent || 0}</p>
                </div>
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div className="bg-green-50 p-3 rounded-lg border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-medium text-green-700">Delivered</p>
                  <p className="text-xl font-bold text-green-900 mt-0.5">{metrics.delivered}</p>
                  <p className="text-[10px] text-green-600">{deliveredRate}% success</p>
                </div>
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-medium text-purple-700">Read</p>
                  <p className="text-xl font-bold text-purple-900 mt-0.5">{metrics.read}</p>
                  <p className="text-[10px] text-purple-600">{readRate}% engagement</p>
                </div>
                <Eye className="h-6 w-6 text-purple-600" />
              </div>
            </div>
            <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-medium text-emerald-700">Paid</p>
                  <p className="text-xl font-bold text-emerald-900 mt-0.5">{metrics.paidAfter || 0}</p>
                  <p className="text-[10px] text-emerald-600">{conversionRate}% conversion</p>
                </div>
                <DollarSign className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </div>

          {/* Progress Bars */}
          <div className="space-y-2">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-gray-700">Delivery Rate</span>
                <span className="text-xs font-bold text-green-600">{deliveredRate}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div className="bg-green-500 h-1.5 rounded-full" style={{ width: `${deliveredRate}%` }} />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-gray-700">Read Rate</span>
                <span className="text-xs font-bold text-purple-600">{readRate}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div className="bg-purple-500 h-1.5 rounded-full" style={{ width: `${readRate}%` }} />
              </div>
            </div>
          </div>

          {/* Fee Structure Info */}
          {feeStructure && (
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <h3 className="text-xs font-semibold text-gray-900 mb-2 flex items-center gap-1.5">
                <Layers className="h-3.5 w-3.5 text-gray-600" />
                Fee Structure Details
              </h3>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <p className="text-[10px] text-gray-500">Academic Year</p>
                  <p className="font-medium text-gray-900">{feeStructure.academicYear}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-500">Total Amount</p>
                  <p className="font-medium text-gray-900">₹{feeStructure.totalAmount?.toLocaleString('en-IN')}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-500">Categories</p>
                  <p className="font-medium text-gray-900">{feeStructure.categories?.length || 0}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-500">Status</p>
                  <p className="font-medium capitalize text-gray-900">{campaign.status}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// =========================================================================
// CAMPAIGN FORM MODAL
// =========================================================================
const CampaignFormModal = ({ campaign, onClose, onSubmit, isEdit, feeStructures }) => {
  // Removed useData call for feeStructures
  const [formData, setFormData] = useState({
    feeStructureId: campaign?.feeStructureId || '',
    recipientType: campaign?.recipientType || (localStorage.getItem('organizationType')?.toUpperCase() === 'COLLEGE' ? 'both' : 'parent'),
    scheduleDate: campaign?.scheduleDate ? new Date(campaign.scheduleDate).toISOString().slice(0, 16) : '',
    sendNow: !campaign
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.feeStructureId) {
      toast.error('Please select a fee structure');
      return;
    }

    if (!formData.sendNow && !formData.scheduleDate) {
      toast.error('Please select a schedule date');
      return;
    }

    onSubmit({
      ...formData,
      scheduleDate: formData.scheduleDate || new Date().toISOString()
    });
  };

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900">
            {isEdit ? 'Edit Reminder Campaign' : 'Send Fee Reminders'}
          </h3>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Fee Structure <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.feeStructureId}
              onChange={(e) => setFormData(prev => ({ ...prev, feeStructureId: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              required
              disabled={isEdit}
            >
              <option value="">-- Select Structure --</option>
              {feeStructures.map(fs => (
                <option key={fs._id} value={fs._id}>
                  {fs.name} - ₹{fs.totalAmount?.toLocaleString('en-IN')} ({fs.academicYear})
                </option>
              ))}
            </select>
          </div>

          <div className="py-2">
            <RecipientSelector
              value={formData.recipientType}
              onChange={(val) => setFormData(prev => ({ ...prev, recipientType: val }))}
              label="Send Reminders To"
              size="sm"
            />
          </div>

          {!isEdit && (
            <div className="bg-gray-50 p-3 rounded">
              <label className="flex items-center gap-2 mb-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.sendNow}
                  onChange={(e) => setFormData(prev => ({ ...prev, sendNow: e.target.checked }))}
                  className="rounded text-indigo-600 focus:ring-indigo-500 h-3.5 w-3.5"
                />
                <span className="font-medium text-sm">Send Immediately</span>
              </label>

              {!formData.sendNow && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Schedule DateTime</label>
                  <input
                    type="datetime-local"
                    value={formData.scheduleDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, scheduleDate: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500"
                    min={new Date().toISOString().slice(0, 16)}
                    required
                  />
                </div>
              )}
            </div>
          )}

          {isEdit && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Reschedule DateTime</label>
              <input
                type="datetime-local"
                value={formData.scheduleDate}
                onChange={(e) => setFormData(prev => ({ ...prev, scheduleDate: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500"
                min={new Date().toISOString().slice(0, 16)}
                required
              />
            </div>
          )}

          <div className="flex justify-end gap-2 pt-3">
            <button type="button" onClick={onClose} className="px-3 py-1.5 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50 font-medium">
              Cancel
            </button>
            <button type="submit" className="btn-primary text-sm flex items-center gap-1.5">
              {isEdit ? <><Edit className="h-3.5 w-3.5" /> Update</> : formData.sendNow ? <><Send className="h-3.5 w-3.5" /> Send Now</> : <><Clock className="h-3.5 w-3.5" /> Schedule</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// =========================================================================
// CAMPAIGN ITEM (Collapsible like FeeStructureItem)
// =========================================================================
const CampaignItem = ({ campaign, feeStructure, onView, onEdit, onDelete }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const metrics = campaign.metrics || { total: 0, delivered: 0, failed: 0, read: 0 };
  const deliveryRate = metrics.total > 0 ? ((metrics.delivered / metrics.total) * 100).toFixed(1) : 0;

  const getStatusConfig = (status) => {
    switch (status) {
      case 'sent':
        return { bg: 'bg-green-100', text: 'text-green-600', label: 'Sent' };
      case 'scheduled':
        return { bg: 'bg-blue-100', text: 'text-blue-600', label: 'Scheduled' };
      case 'failed':
        return { bg: 'bg-red-100', text: 'text-red-600', label: 'Failed' };
      default:
        return { bg: 'bg-gray-100', text: 'text-gray-600', label: status };
    }
  };

  const statusConfig = getStatusConfig(campaign.status);

  return (
    <div className="bg-white border border-gray-200 rounded hover:border-indigo-300 transition-all duration-200 overflow-hidden">
      {/* Header / Summary */}
      <div 
        onClick={() => setIsExpanded(!isExpanded)}
        className="p-3 flex items-center justify-between cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3 flex-1">
          <div className={`p-1.5 rounded ${statusConfig.bg}`}>
            <Bell className={`h-4 w-4 ${statusConfig.text}`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-gray-900">{feeStructure?.name || 'Fee Reminder'}</h3>
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${statusConfig.bg} ${statusConfig.text}`}>
                {statusConfig.label}
              </span>
            </div>
            <div className="flex items-center gap-3 text-[10px] text-gray-500 mt-0.5">
              <span className="flex items-center gap-1"><Calendar className="h-3 w-3"/> {campaign.scheduleDate ? new Date(campaign.scheduleDate).toLocaleDateString() : 'Not scheduled'}</span>
              <span className="flex items-center gap-1"><Users className="h-3 w-3"/> {metrics.total} recipients</span>
              {campaign.status === 'sent' && <span className="font-medium text-green-600">{deliveryRate}% delivered</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right hidden sm:block">
            <div className="text-[10px] text-gray-500">Structure</div>
            <div className="text-xs font-medium text-gray-900">{feeStructure?.academicYear || 'N/A'}</div>
          </div>
          {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="p-3 border-t border-gray-200 bg-white">
          {/* Fee Structure Info */}
          {feeStructure && (
            <div className="mb-3 bg-gray-50 p-2.5 rounded">
              <span className="text-[10px] font-medium text-gray-500 uppercase">Fee Structure Details</span>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-1.5 text-xs">
                <div>
                  <p className="text-[10px] text-gray-500">Amount</p>
                  <p className="font-medium text-gray-900">₹{feeStructure.totalAmount?.toLocaleString('en-IN')}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-500">Categories</p>
                  <p className="font-medium text-gray-900">{feeStructure.categories?.length || 0}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-500">Recipient</p>
                  <p className="font-medium text-gray-900 capitalize">{campaign.recipientType}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-500">Scheduled</p>
                  <p className="font-medium text-gray-900">
                    {campaign.scheduleDate ? new Date(campaign.scheduleDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '-'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Metrics (if sent) */}
          {campaign.status === 'sent' && metrics.total > 0 && (
            <div className="mb-3">
              <span className="text-[10px] font-medium text-gray-500 uppercase">Delivery Metrics</span>
              <div className="grid grid-cols-3 gap-2 mt-1.5">
                <div className="bg-green-50 p-2 rounded text-center border border-green-100">
                  <p className="text-lg font-bold text-green-600">{metrics.delivered}</p>
                  <p className="text-[10px] text-gray-600">Delivered</p>
                </div>
                <div className="bg-blue-50 p-2 rounded text-center border border-blue-100">
                  <p className="text-lg font-bold text-blue-600">{metrics.read}</p>
                  <p className="text-[10px] text-gray-600">Read</p>
                </div>
                <div className="bg-red-50 p-2 rounded text-center border border-red-100">
                  <p className="text-lg font-bold text-red-600">{metrics.failed}</p>
                  <p className="text-[10px] text-gray-600">Failed</p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
            <button
              onClick={(e) => { e.stopPropagation(); onView(campaign); }}
              className="px-2.5 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded transition-colors flex items-center gap-1"
            >
              <Eye className="h-3.5 w-3.5" /> View Details
            </button>
            {campaign.status !== 'sent' && (
              <button
                onClick={(e) => { e.stopPropagation(); onEdit(campaign); }}
                className="px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 rounded transition-colors flex items-center gap-1"
              >
                <Edit className="h-3.5 w-3.5" /> Edit
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(campaign._id); }}
              className="px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded transition-colors flex items-center gap-1"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// =========================================================================
// MAIN COMPONENT
// =========================================================================
const FeeReminders = ({ feeStructures = [] }) => {
  const { whatsappConfigured } = useData();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, campaignId: null });
  const [isDeleting, setIsDeleting] = useState(false);

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const res = await feeApi.getReminderCampaigns();
      if (res.success) {
        setCampaigns(res.data || []);
      }
    } catch (error) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCampaigns();
  }, []);

  const handleCreateCampaign = async (data) => {
    try {
      const res = await feeApi.createReminderCampaign(data);
      if (res.success) {
        toast.success(data.sendNow ? 'Reminders sent successfully!' : 'Campaign scheduled!');
        setShowFormModal(false);
        loadCampaigns();
      } else {
        toast.error(res.message || 'Failed to create campaign');
      }
    } catch (error) {
      toast.error('Failed to create campaign');
    }
  };

  const handleEditCampaign = async (data) => {
    try {
      const res = await feeApi.updateReminderCampaign(editingCampaign._id, data);
      if (res.success) {
        toast.success('Campaign updated successfully!');
        setEditingCampaign(null);
        setShowFormModal(false);
        loadCampaigns();
      } else {
        toast.error(res.message || 'Failed to update campaign');
      }
    } catch (error) {
      toast.error('Failed to update campaign');
    }
  };

  const handleDeleteCampaign = (campaignId) => {
    setDeleteModal({ open: true, campaignId });
  };

  const confirmDeleteCampaign = async () => {
    if (!deleteModal.campaignId) return;
    setIsDeleting(true);
    try {
      const res = await feeApi.deleteReminderCampaign(deleteModal.campaignId);
      if (res.success) {
        toast.success('Campaign deleted');
        setDeleteModal({ open: false, campaignId: null });
        loadCampaigns();
      } else {
        toast.error(res.message || 'Failed to delete');
      }
    } catch (error) {
      toast.error('Failed to delete campaign');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="card bg-white p-4 rounded shadow-md border border-gray-200">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <div className="p-2 bg-yellow-100 rounded"><Bell className="h-4 w-4 text-yellow-600"/></div>
          <div className="ml-2">
            <h2 className="text-base font-semibold text-gray-900">Fee Reminder Campaigns</h2>
            <p className="text-xs text-gray-500">Manage automated WhatsApp reminders</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={loadCampaigns}
            disabled={loading}
            className="px-2.5 py-1.5 border border-gray-300 rounded text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 flex items-center gap-1.5"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {whatsappConfigured && (
            <button 
              onClick={() => { setEditingCampaign(null); setShowFormModal(true); }}
              className="btn-primary text-xs shadow-sm flex items-center gap-1.5"
            >
              <Plus className="h-3.5 w-3.5" />
              Send Reminders
            </button>
          )}
        </div>
      </div>

      {/* Campaigns List */}
      {loading ? (
        <div className="py-8 text-center text-xs text-gray-500">Loading campaigns...</div>
      ) : campaigns.length === 0 ? (
        <div className="py-8 text-center text-gray-500 bg-gray-50 rounded border border-dashed border-gray-300">
          <p className="text-xs">No campaigns found.</p>
          {whatsappConfigured && (
            <button onClick={() => setShowFormModal(true)} className="mt-1 text-xs text-indigo-600 hover:underline">
              Send your first reminder
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {campaigns.map(campaign => (
            <CampaignItem
              key={campaign._id}
              campaign={campaign}
              feeStructure={feeStructures.find(fs => fs._id === campaign.feeStructureId)}
              onView={(c) => setSelectedCampaign(c)}
              onEdit={(c) => { setEditingCampaign(c); setShowFormModal(true); }}
              onDelete={handleDeleteCampaign}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {showFormModal && (
        <CampaignFormModal
          campaign={editingCampaign}
          isEdit={!!editingCampaign}
          onClose={() => { setShowFormModal(false); setEditingCampaign(null); }}
          onSubmit={editingCampaign ? handleEditCampaign : handleCreateCampaign}
          feeStructures={feeStructures}
        />
      )}
      {selectedCampaign && (
        <CampaignDetailModal
          campaign={selectedCampaign}
          feeStructure={feeStructures.find(fs => fs._id === selectedCampaign.feeStructureId)}
          onClose={() => setSelectedCampaign(null)}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, campaignId: null })}
        onConfirm={confirmDeleteCampaign}
        title="Delete Reminder Campaign"
        message="Are you sure you want to delete this reminder campaign? This action cannot be undone and will stop any scheduled messages."
        confirmText="Delete Campaign"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default FeeReminders;